
import { WordRow } from "../WordRow";
import GameCompletionScreen from "../GameCompletionScreen";
import { ProgressTracker } from "../ProgressTracker";
import { PrevLevelAnswer } from "../PrevLevelAnswer";
import { LetterBox } from "../LetterBox";
import { getMaxAttemptsForLevel, calculateLevelFromWordLength } from "./GameLogic";
import { useMemo, useState, memo, useCallback, useRef, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useGameMode } from "@/contexts/GameModeContext";

type KeyboardStatusType = {
  [key: string]: "correct" | "wrong-position" | "incorrect" | "empty";
};

interface GameBoardViewProps {
  currentWord: string;
  guesses: {
    letter: string;
    status: string;
  }[][];
  currentGuess: string;
  keyboardStatus: KeyboardStatusType;
  isValidating: boolean;
  previousWord: {
    word: string;
    guess: {
      letter: string;
      status: string;
    }[];
  } | null;
  isTransitioning: boolean;
  showGameCompletion: boolean;
  setShowGameCompletion: (show: boolean) => void;
  gameStatus: string;
  guessHistory: {
    level: number;
    word: string;
    guesses: {
      letter: string;
      status: string;
    }[][];
  }[];
  invalidGuessType: "duplicate" | "invalid" | null;
  levelAttempts: number[];
  handleSubmitGuess: () => void;
  immediateCompletion?: boolean;
  compactLayout?: boolean;
}

const areArraysEqual = (a: any[], b: any[]): boolean => {
  if (a.length !== b.length) return false;
  return a.every((item, index) => {
    if (Array.isArray(item) && Array.isArray(b[index])) {
      return areArraysEqual(item, b[index]);
    }
    if (typeof item === 'object' && item !== null) {
      return JSON.stringify(item) === JSON.stringify(b[index]);
    }
    return item === b[index];
  });
};

export const GameBoardView = memo(({
  currentWord,
  guesses,
  currentGuess,
  keyboardStatus,
  isValidating,
  previousWord,
  isTransitioning,
  showGameCompletion,
  setShowGameCompletion,
  gameStatus,
  guessHistory,
  invalidGuessType,
  levelAttempts,
  handleSubmitGuess,
  immediateCompletion = false,
  compactLayout = false
}: GameBoardViewProps) => {
  const { currentMode } = useGameMode();
  const isPracticeMode = currentMode === 'practice';
  const [animationComplete, setAnimationComplete] = useState(false);
  const animationCompletedRef = useRef(false);
  const currentLevel = useMemo(() => currentWord ? calculateLevelFromWordLength(currentWord.length) : 1, [currentWord]);
  const shouldShowPreviousWord = useMemo(() => previousWord && previousWord.word !== "", [previousWord]);
  const maxAttempts = useMemo(() => getMaxAttemptsForLevel(currentWord?.length || 3), [currentWord]);

  useEffect(() => {
    if (gameStatus === "playing") {
      setAnimationComplete(false);
      animationCompletedRef.current = false;
    }
  }, [gameStatus]);

  useEffect(() => {
    if (isTransitioning) {
      console.log('GameBoardView - Transition detected', {
        currentLevel, 
        currentWord,
        previousWord: previousWord?.word
      });
    }
  }, [isTransitioning, currentLevel, currentWord, previousWord]);

  const handleAnimationComplete = useCallback(() => {
    console.log('GameBoardView - Animation complete callback triggered');
    
    // Prevent duplicate triggers
    if (animationCompletedRef.current) return;
    animationCompletedRef.current = true;
    
    setAnimationComplete(true);
    
    // Delay showing game completion screen to allow for smooth transitions
    if (gameStatus === "lost") {
      console.log('GameBoardView - Showing game completion screen after animation delay');
      // Shorter delay for better user experience
      setTimeout(() => {
        setShowGameCompletion(true);
      }, 600); 
    }
  }, [gameStatus, setShowGameCompletion]);

  const wordRows = useMemo(() => Array.from({
    length: maxAttempts
  }).map((_, index) => {
    const isCurrentRow = index === guesses.length;
    const isLastRow = index === maxAttempts - 1;
    const isUltimateGuess = isLastRow && index === guesses.length - 1;
    
    return (
      <WordRow 
        key={index} 
        word={currentWord} 
        guess={index < guesses.length ? guesses[index] : null} 
        currentGuess={isCurrentRow ? currentGuess : null} 
        isRevealed={index < guesses.length} 
        isActive={true} 
        isInvalid={isCurrentRow && invalidGuessType !== null} 
        isCorrectGuess={index === guesses.length - 1 && guesses[index]?.every(g => g.status === "correct")} 
        isLastIncorrectGuess={isLastRow && index === guesses.length - 1 && gameStatus === "lost"} 
        isUltimateGuess={isUltimateGuess}
        onAnimationComplete={gameStatus === "lost" && isUltimateGuess ? handleAnimationComplete : undefined} 
      />
    );
  }), [currentWord, guesses, currentGuess, maxAttempts, invalidGuessType, gameStatus, handleAnimationComplete]);

  function EmptyPlaceholderContainer() {
    return (
      <div className="mb-1 px-3 py-1 sm:py-2 w-full rounded-b-xl shadow-lg bg-gradient-to-b from-black/80 to-black/60">
        <div className="flex justify-center space-x-2">
          {Array.from({length: 3}).map((_, index) => (
            <LetterBox key={index} status="empty" isCompact={true} isPlaceholder={true} />
          ))}
        </div>
      </div>
    );
  }
  EmptyPlaceholderContainer.displayName = 'EmptyPlaceholderContainer';

  console.log('GameBoardView render:', {
    gameStatus,
    showGameCompletion,
    animationComplete,
    currentLevel,
    hasPreviousWord: !!previousWord,
    isTransitioning
  });

  return (
    <div className={`space-y-1 ${compactLayout ? "livestream-board-view" : ""}`}>
      <div className="flex flex-col">
        <ProgressTracker 
          currentLevel={currentLevel} 
          completedLevels={levelAttempts.map((_, index) => index + 1)} 
          failedLevel={gameStatus === "lost" ? currentLevel : undefined} 
        />

        <div className="mb-1">
          {currentLevel === 1 
            ? <EmptyPlaceholderContainer /> 
            : shouldShowPreviousWord && previousWord 
              ? <PrevLevelAnswer word={previousWord.word} guess={previousWord.guess} /> 
              : <EmptyPlaceholderContainer />
          }
        </div>
      </div>

      <div className={`${compactLayout ? "livestream-game-board-container" : "h-[360px]"} game-board-container flex flex-col justify-between mb-1`}>
        <div className="grid gap-1">
          {wordRows}
        </div>

        <div className="text-xs text-gray-400 text-center mt-0">
          {maxAttempts - guesses.length} attempts remaining
        </div>
      </div>

      <GameCompletionScreen 
        levelAttempts={levelAttempts} 
        isOpen={showGameCompletion} 
        onClose={() => setShowGameCompletion(false)} 
        failedAtLevel={gameStatus === "lost" ? currentLevel : undefined} 
        guessHistory={guessHistory}
        immediate={immediateCompletion}
      />
    </div>
  );
}, (prevProps, nextProps) => {
  if (prevProps.currentWord !== nextProps.currentWord || prevProps.currentGuess !== nextProps.currentGuess || prevProps.isValidating !== nextProps.isValidating || prevProps.isTransitioning !== nextProps.isTransitioning || prevProps.showGameCompletion !== nextProps.showGameCompletion || prevProps.gameStatus !== nextProps.gameStatus || prevProps.invalidGuessType !== nextProps.invalidGuessType || prevProps.immediateCompletion !== nextProps.immediateCompletion || prevProps.compactLayout !== nextProps.compactLayout) {
    return false;
  }
  if (!areArraysEqual(prevProps.guesses, nextProps.guesses)) return false;
  if (!areArraysEqual(prevProps.levelAttempts, nextProps.levelAttempts)) return false;
  if (!areArraysEqual(prevProps.guessHistory, nextProps.guessHistory)) return false;
  const prevKeyboardKeys = Object.keys(prevProps.keyboardStatus);
  const nextKeyboardKeys = Object.keys(nextProps.keyboardStatus);
  if (prevKeyboardKeys.length !== nextKeyboardKeys.length) return false;
  if (!prevKeyboardKeys.every(key => prevProps.keyboardStatus[key] === nextProps.keyboardStatus[key])) return false;
  if (prevProps.previousWord === null && nextProps.previousWord === null) return true;
  if (prevProps.previousWord === null || nextProps.previousWord === null) return false;
  if (prevProps.previousWord.word !== nextProps.previousWord.word) return false;
  return areArraysEqual(prevProps.previousWord.guess, nextProps.previousWord.guess);
});

GameBoardView.displayName = 'GameBoardView';
